import React, {useEffect, useMemo, useState} from 'react';
import DocumentTitle from 'react-document-title';
import {useNavigate, useParams} from 'react-router-dom';

import isEmpty from 'lodash.isempty';
import {cleanChangeRequestReview} from 'src/modules/ai/change-request-review/services/actions';
import {UserFeedbackModalController} from 'src/modules/ai/user-feedback/user-feedback-modal.controller';
import {cleanAttributesInfo} from 'src/modules/attributes/services/actions';
import {cleanChangeRequests} from 'src/modules/change-requests/services/actions';
import {createEventRoutePath} from 'src/modules/event/event-route-planner';
import {EventCreationMode} from 'src/modules/event-create/constants';
import {createEventCreationRoutePath} from 'src/modules/event-create/event-creation-route-planner';
import {getChangeRequestInfo} from 'src/modules/event-create/services';
import {PushNotificationIds, SuccessNotification} from 'src/modules/event-review/constants';
import {EventReviewView} from 'src/modules/event-review/event-review.view';
import {useEventInfo} from 'src/modules/event-review/hooks/useEventInfo';
import {usePreviouslyApprovedEventInfo} from 'src/modules/event-review/hooks/usePreviouslyApprovedEventInfo';
import {getPageTitle} from 'src/modules/event-review/utils/getPageTitle';
import {sortAttributes} from 'src/modules/event-review/utils/sortAttributes';
import {cleanEventsInfo} from 'src/modules/events/services/actions';
import {cleanCounts} from 'src/modules/tabs-panel/services/actions';
import {UsersInfoController} from 'src/modules/users-info/users-info.controller';
import {getChangeRequestById} from 'src/openapi/apis/ChangeRequestControllerApi';
import {
    approve,
    reject,
    reopen,
    sendToApprove,
    startReview,
    stopReview,
} from 'src/openapi/apis/ChangeRequestStatusControllerApi';
import {getTemplate, getTemplateSuccess} from 'src/openapi/apis/EventTemplateControllerApi';
import {AttributeTemplate} from 'src/openapi/models/AttributeTemplate';
import {ChangeRequestStatus} from 'src/openapi/models/ChangeRequestStatus';
import {useAppDispatch, useAppSelector} from 'src/store';

import {Icons} from '@pb/kit-component-icon';
import {Loading} from '@pb/kit-component-loading';
import {Status, usePushNotification} from '@pb/kit-component-push-notification';

import {useAiUsage} from '../ai';

const SUCCESS_AUTO_HIDE_TIMEOUT = 2000;
const ERROR_AUTO_HIDE_TIMEOUT = 10000;

export const EventReviewController = () => {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const changeRequestId = parseInt(useParams<{changeRequestId: string}>()?.changeRequestId ?? '');
    const changeRequest = useAppSelector(getChangeRequestInfo)?.[changeRequestId];
    const event = changeRequest?.event;
    const eventId = Number(event?.id);
    const eventSource = event?.source ?? '';
    const eventCode = event?.code || '';
    const eventType = event?.type || '';
    const previouslyApprovedEvent = usePreviouslyApprovedEventInfo(changeRequest);
    const existingEvent = useEventInfo(eventCode, eventSource, eventType);
    const {onCreateNote} = usePushNotification();
    const [sourceSpecificAttributesNames, setSourceSpecificAttributesNames] = useState<string[]>([]);
    const [showUserFeedbackForm, setShowUserFeedbackForm] = useState(false);
    const {checkFeedbackModal} = useAiUsage(changeRequestId);
    const isAiUsed = checkFeedbackModal();

    const eventReviewSortedAttributes = useMemo(() => {
        if (!changeRequest) {
            return changeRequest;
        }

        return sortAttributes(changeRequest, sourceSpecificAttributesNames);
    }, [changeRequest, sourceSpecificAttributesNames]);

    useEffect(() => {
        if (eventSource) {
            dispatch(getTemplate({eventSource, eventType})).then(e => {
                if (!e.payload.error && e.type === getTemplateSuccess.type) {
                    const attributesTemplate = e.payload.data.attributes as AttributeTemplate[];
                    setSourceSpecificAttributesNames(attributesTemplate.map(attr => attr.name));
                }
            });
        }
    }, [dispatch, eventSource, eventType]);

    useEffect(() => {
        if (!changeRequest) {
            dispatch(getChangeRequestById({id: changeRequestId}));
        }
    }, [changeRequest, changeRequestId, dispatch]);

    useEffect(() => {
        dispatch(cleanChangeRequestReview());
    }, [dispatch]);

    useEffect(() => {
        if (changeRequestId && isAiUsed) {
            setShowUserFeedbackForm(true);
        }
    }, [changeRequestId, isAiUsed]);

    const onStartReview = async () => {
        const response = await dispatch(startReview({changeRequestId}));

        if (response.payload.success) {
            onCreateNote({
                id: PushNotificationIds.startReviewSuccess,
                status: Status.SUCCESS,
                message: SuccessNotification[ChangeRequestStatus.IN_REVIEW],
                buttonProps: {iconProps: {type: Icons.CLOSE}},
                autoHideTimeout: SUCCESS_AUTO_HIDE_TIMEOUT,
            });
            dispatch(getChangeRequestById({id: changeRequestId}));
            dispatch(cleanChangeRequestReview());
            dispatch(cleanChangeRequests());
        }

        if (response.payload.error) {
            onCreateNote({
                id: PushNotificationIds.startReviewFailure,
                status: Status.ERROR,
                message: response.payload.data.message,
                buttonProps: {iconProps: {type: Icons.CLOSE}},
                autoHideTimeout: ERROR_AUTO_HIDE_TIMEOUT,
            });
        }
    };

    const onStopReview = async () => {
        const response = await dispatch(stopReview({changeRequestId}));

        if (response.payload.success) {
            onCreateNote({
                id: PushNotificationIds.stopReviewSuccess,
                status: Status.SUCCESS,
                message: SuccessNotification[ChangeRequestStatus.WAITING_REVIEW],
                buttonProps: {iconProps: {type: Icons.CLOSE}},
                autoHideTimeout: SUCCESS_AUTO_HIDE_TIMEOUT,
            });
            dispatch(getChangeRequestById({id: changeRequestId}));
            dispatch(cleanChangeRequests());
            dispatch(cleanChangeRequestReview());
        }

        if (response.payload.error) {
            onCreateNote({
                id: PushNotificationIds.stopReviewFailure,
                status: Status.ERROR,
                message: response.payload.data.message,
                buttonProps: {iconProps: {type: Icons.CLOSE}},
                autoHideTimeout: ERROR_AUTO_HIDE_TIMEOUT,
            });
        }
    };

    const onSendToApprove = async () => {
        const response = await dispatch(sendToApprove({changeRequestId}));

        if (response.payload.success) {
            onCreateNote({
                id: PushNotificationIds.sendToApproveSuccess,
                status: Status.SUCCESS,
                message: SuccessNotification[ChangeRequestStatus.WAITING_APPROVE],
                buttonProps: {iconProps: {type: Icons.CLOSE}},
                autoHideTimeout: SUCCESS_AUTO_HIDE_TIMEOUT,
            });
            dispatch(getChangeRequestById({id: changeRequestId}));
            dispatch(cleanChangeRequests());
            dispatch(cleanChangeRequestReview());
        }

        if (response.payload.error) {
            onCreateNote({
                id: PushNotificationIds.sendToApproveFailure,
                status: Status.ERROR,
                message: response.payload.data.message,
                buttonProps: {iconProps: {type: Icons.CLOSE}},
                autoHideTimeout: ERROR_AUTO_HIDE_TIMEOUT,
            });
        }
    };

    const onApprove = async () => {
        const response = await dispatch(approve({changeRequestId}));

        if (response.payload.success) {
            onCreateNote({
                id: PushNotificationIds.approveSuccess,
                status: Status.SUCCESS,
                message: SuccessNotification[ChangeRequestStatus.APPROVED],
                buttonProps: {iconProps: {type: Icons.CLOSE}},
                autoHideTimeout: SUCCESS_AUTO_HIDE_TIMEOUT,
            });
            dispatch(getChangeRequestById({id: changeRequestId}));
            dispatch(cleanChangeRequests());
            dispatch(cleanEventsInfo());
            dispatch(cleanAttributesInfo());
            dispatch(cleanCounts());
        }

        if (response.payload.error) {
            onCreateNote({
                id: PushNotificationIds.approveFailure,
                status: Status.ERROR,
                message: response.payload.data.message,
                buttonProps: {iconProps: {type: Icons.CLOSE}},
                autoHideTimeout: ERROR_AUTO_HIDE_TIMEOUT,
            });
        }
    };

    const onReopen = async (openUpdateForm: boolean) => {
        const response = await dispatch(reopen({changeRequestId}));

        if (response.payload.success) {
            onCreateNote({
                id: PushNotificationIds.reopenSuccess,
                status: Status.SUCCESS,
                message: SuccessNotification[ChangeRequestStatus.REOPENED],
                buttonProps: {iconProps: {type: Icons.CLOSE}},
                autoHideTimeout: SUCCESS_AUTO_HIDE_TIMEOUT,
            });
            dispatch(getChangeRequestById({id: changeRequestId}));
            dispatch(cleanChangeRequests());
            dispatch(cleanChangeRequestReview());

            if (openUpdateForm) {
                const eventCreationRoutePath = createEventCreationRoutePath({
                    mode: eventId ? EventCreationMode.update : EventCreationMode.create,
                    eventId: eventId || 0,
                    changeRequestId: changeRequestId || 0,
                });

                navigate(eventCreationRoutePath);
            }
        }

        if (response.payload.error) {
            onCreateNote({
                id: PushNotificationIds.reopenFailure,
                status: Status.ERROR,
                message: response.payload.data.message,
                buttonProps: {iconProps: {type: Icons.CLOSE}},
                autoHideTimeout: ERROR_AUTO_HIDE_TIMEOUT,
            });
        }
    };

    const onReject = async () => {
        const response = await dispatch(reject({changeRequestId}));

        if (response.payload.success) {
            onCreateNote({
                id: PushNotificationIds.rejectSuccess,
                status: Status.SUCCESS,
                message: SuccessNotification[ChangeRequestStatus.REJECTED],
                buttonProps: {iconProps: {type: Icons.CLOSE}},
                autoHideTimeout: SUCCESS_AUTO_HIDE_TIMEOUT,
            });
            dispatch(getChangeRequestById({id: changeRequestId}));
            dispatch(cleanChangeRequests());
            dispatch(cleanChangeRequestReview());
            dispatch(cleanCounts());
        }

        if (response.payload.error) {
            onCreateNote({
                id: PushNotificationIds.rejectFailure,
                status: Status.ERROR,
                message: response.payload.data.message,
                buttonProps: {iconProps: {type: Icons.CLOSE}},
                autoHideTimeout: ERROR_AUTO_HIDE_TIMEOUT,
            });
        }
    };

    const onShowUserFeedbackForm = () => {
        setShowUserFeedbackForm(true);
    };

    const seeEventHandler = () => {
        if (existingEvent) {
            const eventEventRoutePath = createEventRoutePath({eventId: existingEvent.id});
            window.open(eventEventRoutePath);
        }
    };

    return isEmpty(eventReviewSortedAttributes) || isEmpty(previouslyApprovedEvent) && changeRequest.event.id ?
            <Loading fast={true}/> : (
                <>
                    <DocumentTitle title={getPageTitle(changeRequest.event.code, changeRequest.status)}/>
                    <UsersInfoController {...{changeRequestId}}/>
                    <UserFeedbackModalController
                        changeRequestId={changeRequestId}
                        show={showUserFeedbackForm}
                        setShow={setShowUserFeedbackForm}
                    />
                    <EventReviewView
                        changeRequest={eventReviewSortedAttributes}
                        {...{
                            onStartReview,
                            onStopReview,
                            onSendToApprove,
                            onApprove,
                            onReopen,
                            onReject,
                            onShowUserFeedbackForm,
                            seeEventHandler,
                            eventCodeInfo: previouslyApprovedEvent,
                            changeRequestId,
                            existingEvent,
                        }}
                    />
                </>
            );
};
